import type { CityConfig } from "@/pages/city-template/types";

export const woodstockVtConfig: CityConfig = {
  city: "Woodstock",
  state: "Vermont",
  stateAbbr: "VT",
  slug: "woodstock-vt",
  route: "/cities/woodstock-vt",
  title: "Expedited Passport Services in Woodstock, VT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Woodstock, VT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Woodstock, Quechee, Hartford, White River Junction, and all of the Upper Valley.",
  h1: "Expedited Passport Services in Woodstock, VT",
  heroSubtitle:
    "Woodstock travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No trip to the New York Passport Agency required.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Beautiful%20historic%20Woodstock%20Vermont%20village%20with%20iconic%20covered%20Middle%20Covered%20Bridge%20over%20Ottauquechee%20River%2C%20charming%20white%20church%20with%20tall%20steeple%2C%20classic%20New%20England%20village%20green%20with%20historic%20homes%20and%20buildings%2C%20lush%20green%20rolling%20hills%20of%20the%20Green%20Mountains%20in%20the%20background%2C%20warm%20golden%20hour%20light%2C%20picturesque%20Vermont%20resort%20town%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=vt-wds-hero-001&orientation=landscape",
  heroImageAlt: "Woodstock Vermont village with covered bridge and historic village green",
  geoPosition: "43.6245; -72.5190",
  geoPlacename: "Woodstock, Vermont, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~230 miles from Woodstock via I-91",
  },
  nearbyCitiesZips: [
    { name: "Woodstock", zips: "05091" },
    { name: "Quechee", zips: "05091" },
    { name: "Hartford", zips: "05047, 05001" },
    { name: "White River Junction", zips: "05001" },
    { name: "Hartland", zips: "05048" },
    { name: "Pomfret", zips: "05091" },
    { name: "Reading", zips: "05062" },
    { name: "Bridgewater", zips: "05034" },
    { name: "Killington", zips: "05751" },
    { name: "Ludlow", zips: "05149" },
    { name: "Rutland", zips: "05701, 05702" },
    { name: "Springfield", zips: "05156" },
    { name: "Windsor", zips: "05089" },
    { name: "Bellows Falls", zips: "05101" },
    { name: "West Windsor", zips: "05089" },
    { name: "Norwich", zips: "05055" },
    { name: "Lebanon", zips: "03766, 03784" },
    { name: "Hanover", zips: "03755" },
    { name: "Montpelier", zips: "05601, 05602, 05603, 05604, 05609, 05620, 05633" },
    { name: "Burlington", zips: "05401, 05402, 05405, 05406" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Woodstock travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Upper Valley",
      desc: "Woodstock, Quechee, Hartford, White River Junction, and all surrounding Upper Valley communities.",
    },
  ],
  testimonials: [
    {
      name: "Catherine M.",
      location: "Woodstock, VT",
      rating: 5,
      text: "I needed my passport renewed for a trip to the UK in 5 days. Fast Passport Center had it back to me in Woodstock in 3 days. Their Upper Valley service is outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "Thomas A.",
      location: "Quechee, VT",
      rating: 5,
      text: "Applied for my first passport through Fast Passport Center. They helped me navigate the process from Quechee and I had my passport in 4 days. Great experience.",
      service: "New Passport",
    },
    {
      name: "Patricia N.",
      location: "Hartford, VT",
      rating: 5,
      text: "My daughter needed a passport for a school trip to Europe. Fast Passport Center handled the child passport process and had it ready in 4 days. Hartford families can trust them.",
      service: "Child Passport",
    },
    {
      name: "Daniel F.",
      location: "Hartland, VT",
      rating: 5,
      text: "Lost my passport before a business trip to Asia. Fast Passport Center replaced it in 3 days and saved my trip. Hartland residents should use them for emergencies.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "wds-1",
      name: "Woodstock Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "12 The Green",
      city: "Woodstock",
      state: "VT",
      zip: "05091",
      phone: "802-457-1400",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Woodstock Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "wds-2",
      name: "White River Junction Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "15 Elm Street",
      city: "White River Junction",
      state: "VT",
      zip: "05001",
      phone: "802-295-2911",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "White River Junction",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "wds-3",
      name: "Hartford Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "2001 Hartford Avenue",
      city: "Hartford",
      state: "VT",
      zip: "05047",
      phone: "802-296-2561",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Hartford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "wds-4",
      name: "Quechee Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "Quechee Main Street",
      city: "Quechee",
      state: "VT",
      zip: "05091",
      phone: "802-457-1400",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Quechee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "wds-5",
      name: "Hartland Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "2 Route 5",
      city: "Hartland",
      state: "VT",
      zip: "05048",
      phone: "802-436-1491",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Hartland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "wds-6",
      name: "Woodstock Town Clerk",
      type: "Town Clerk / Passport Services",
      address: "31 The Green",
      city: "Woodstock",
      state: "VT",
      zip: "05091",
      phone: "802-457-3451",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "Woodstock Town Offices",
      website: "https://www.woodstockvt.gov/",
      websiteLabel: "Town of Woodstock",
      notes: "Provides passport application acceptance services. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Woodstock, VT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the New York Passport Agency if I live in Woodstock?",
      a: "No — if you use Fast Passport Center, you don't need to visit the New York Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Woodstock?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Woodstock, White River Junction, and Hartford. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Woodstock?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Woodstock?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Woodstock?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Woodstock. We serve Woodstock and all of the Upper Valley through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Woodstock for international travel?",
      a: "Lebanon Municipal Airport (LEB) in New Hampshire is the closest at ~15 miles. Burlington International (BTV) is ~55 miles, Albany International (ALB) is ~90 miles, and Boston Logan (BOS) is ~130 miles. Most Woodstock residents use Burlington or Albany for international travel.",
    },
    {
      q: "How much does expedited passport service cost in Woodstock?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Woodstock?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Woodstock?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Woodstock?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Woodstock have a local passport agency?",
      a: "No, Woodstock does not have a regional passport agency. The nearest regional agency is the New York Passport Agency in Manhattan, approximately 230 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Woodstock", address: "various CVS locations in Woodstock area", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — White River Junction", address: "various Walgreens locations in White River Junction", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — White River Junction", address: "various UPS Store locations in White River Junction", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Lebanon NH", address: "various FedEx Office locations in Lebanon NH", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Claremont NH", address: "various Walmart locations in Claremont NH", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "VT DMV — White River Junction",
      address: "100 River Road, White River Junction, VT 05001",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications. Closest DMV to Woodstock.",
      featured: true,
    },
    {
      name: "VT DMV — Springfield",
      address: "100 Mineral Street, Springfield, VT 05156",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Alternative DMV location for Southern Vermont residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "LEB",
      name: "Lebanon Municipal Airport",
      distance: "~15 miles from Woodstock",
      address: "5 Airpark Rd, West Lebanon, NH 03784",
      note: "Closest airport to Woodstock with Cape Air service to Boston. Limited commercial flights.",
      flightUrl: "https://www.lebanonairport.com/",
      flightLabel: "Lebanon Municipal",
      featured: true,
    },
    {
      code: "BTV",
      name: "Burlington International Airport",
      distance: "~55 miles from Woodstock",
      address: "1200 Airport Dr, South Burlington, VT 05403",
      note: "Vermont's primary airport with more flight options. Common for Woodstock residents.",
      flightUrl: "https://www.btv.aero/",
      flightLabel: "Burlington International",
      featured: false,
    },
    {
      code: "ALB",
      name: "Albany International Airport",
      distance: "~90 miles from Woodstock",
      address: "737 Albany Shaker Rd, Albany, NY 12211",
      note: "Alternative for Woodstock travelers with Southwest and other domestic carriers.",
      flightUrl: "https://www.albanyairport.com/",
      flightLabel: "Albany International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Woodstock travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Woodstock residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Woodstock residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Woodstock residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Woodstock, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Woodstock residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Woodstock travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Woodstock residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Woodstock residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Woodstock residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Woodstock." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Woodstock." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Woodstock travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Woodstock travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Woodstock team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Woodstock address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Burlington", route: "/cities/burlington-vt" },
    { name: "Montpelier", route: "/cities/montpelier" },
    { name: "Rutland", route: "/cities/rutland" },
    { name: "Stowe", route: "/cities/stowe" },
    { name: "Brattleboro", route: "/cities/brattleboro" },
    { name: "Bennington", route: "/cities/bennington" },
    { name: "Middlebury", route: "/cities/middlebury" },
    { name: "Woodstock", route: "/cities/woodstock-vt" },
    { name: "Springfield", route: "/cities/springfield-vt" },
    { name: "White River Junction", route: "/cities/white-river-junction" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Woodstock and all of the Upper Valley as a remote courier service. We do not have a physical office in Woodstock, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeDistance: "Remote courier service — Woodstock, VT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Woodstock neighborhoods and surrounding Upper Valley communities. From the historic village to the covered bridge, from the green to the Quechee gorge — we serve every Woodstock community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Woodstock and all surrounding Upper Valley communities. Whether you're in the historic village or a nearby town, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Woodstock customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Woodstock travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Woodstock and Upper Valley travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Woodstock, VT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/vermont",
  statePageName: "Vermont Passport Services",
};

export default woodstockVtConfig;